import { API_BASE_URL } from '../config/api.config';

const PLACEHOLDER_PROPERTY_IMAGE =
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500';

/** Minimum property photos required (seller & agent). */
export const MIN_PROPERTY_IMAGES = 3;

/** Resolve relative upload paths to absolute URLs for previews. */
export const resolvePropertyMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) return trimmed;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;

  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  if (trimmed.startsWith('/')) return `${base}${trimmed}`;
  if (trimmed.startsWith('backend/uploads/')) {
    return `${base}/${trimmed.replace(/^backend\//, '')}`;
  }
  if (trimmed.startsWith('/backend/uploads/')) {
    return `${base}${trimmed.replace(/^\/backend/, '')}`;
  }
  if (trimmed.startsWith('uploads/')) return `${base}/${trimmed}`;
  if (trimmed.startsWith('properties/')) return `${base}/uploads/${trimmed}`;
  return `${base}/uploads/${trimmed}`;
};

export const resolvePropertyImageUrls = (images) => {
  if (!Array.isArray(images)) return [];
  return images.map(extractPropertyImageUrl).filter(Boolean);
};

/** Normalize a single image entry (string, object, or comma-separated string). */
export function extractPropertyImageUrl(entry) {
  if (entry == null) return '';

  if (typeof entry === 'string') {
    const trimmed = entry.trim();
    if (!trimmed) return '';
    if (trimmed.includes(',') && !trimmed.startsWith('data:') && !trimmed.startsWith('http')) {
      const first = trimmed.split(',').map((p) => p.trim()).find(Boolean);
      return first ? resolvePropertyMediaUrl(first) : '';
    }
    return resolvePropertyMediaUrl(trimmed);
  }

  if (typeof entry === 'object') {
    return extractPropertyImageUrl(
      entry.url ||
        entry.image_url ||
        entry.src ||
        entry.path ||
        entry.file_url ||
        entry.cover_image ||
        '',
    );
  }

  return '';
}

/** Collect all usable image URLs from a property/listing record. */
export function collectPropertyImageUrls(property = {}) {
  const candidates = [
    property.cover_image,
    property.display_image,
    property.image,
    property.thumbnail,
    ...(Array.isArray(property.images)
      ? property.images
      : property.images
        ? [property.images]
        : []),
  ];

  const urls = [];
  const seen = new Set();

  candidates.forEach((entry) => {
    if (typeof entry === 'string' && entry.includes(',') && !entry.startsWith('data:') && !entry.startsWith('http')) {
      entry.split(',').forEach((part) => {
        const url = extractPropertyImageUrl(part);
        if (url && !seen.has(url)) {
          seen.add(url);
          urls.push(url);
        }
      });
      return;
    }
    const url = extractPropertyImageUrl(entry);
    if (url && !seen.has(url)) {
      seen.add(url);
      urls.push(url);
    }
  });

  return urls;
}

/** Gallery-ready image objects for details pages. */
export function collectPropertyImageObjects(property = {}, altFallback = 'Property') {
  const alt = property?.title || altFallback;
  const urls = collectPropertyImageUrls(property);
  const orderedUrls = orderImagesWithCoverFirst(urls, property?.cover_image);
  if (orderedUrls.length === 0) {
    return [{ id: 1, url: PLACEHOLDER_PROPERTY_IMAGE, alt }];
  }
  return orderedUrls.map((url, idx) => ({ id: idx + 1, url, alt }));
}

export { PLACEHOLDER_PROPERTY_IMAGE };

/** Move one array item to index 0 (e.g. set listing cover photo). */
export function reorderArrayItemToFront(items, index) {
  if (!Array.isArray(items) || index <= 0 || index >= items.length) {
    return Array.isArray(items) ? items : [];
  }
  const next = [...items];
  const [picked] = next.splice(index, 1);
  next.unshift(picked);
  return next;
}

/** Put cover_image first when it exists in the images list (edit form load). */
export function orderImagesWithCoverFirst(images, coverImage) {
  if (!Array.isArray(images) || images.length === 0) return [];
  const coverUrl = extractPropertyImageUrl(coverImage);
  if (!coverUrl) return images;

  const matchIndex = images.findIndex((img) => extractPropertyImageUrl(img) === coverUrl);
  if (matchIndex <= 0) return images;
  return reorderArrayItemToFront(images, matchIndex);
}

export const buildVideoFromUrl = (videoUrl) => {
  const url = resolvePropertyMediaUrl(videoUrl);
  if (!url) return null;
  const name = decodeURIComponent(url.split('/').pop()?.split('?')[0] || '') || 'Property video';
  return { url, name, size: 0, isExisting: true };
};

export const buildBrochureFromUrl = (brochureUrl) => {
  const url = resolvePropertyMediaUrl(brochureUrl);
  if (!url) return null;
  const name = decodeURIComponent(url.split('/').pop()?.split('?')[0] || '') || 'Brochure.pdf';
  return { url, name, size: 0, isExisting: true };
};

/** Fix URLs/text mangled by htmlspecialchars (& → &amp; → &amp;amp; …). */
export const decodeHtmlEntitiesInUrl = (value) => {
  if (!value || typeof value !== 'string') return value;
  let result = value;
  for (let i = 0; i < 6; i++) {
    const next = result
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    if (next === result) break;
    result = next;
  }
  return result;
};

/** Decode HTML entities in any display string (configurations, labels, etc.). */
export const decodeHtmlEntities = (value) => {
  if (value == null) return value;
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return value;
  return decodeHtmlEntitiesInUrl(value);
};

/** Parse upcoming_project_data from API (object, JSON string, or double-encoded). */
export const parseUpcomingProjectData = (raw) => {
  if (raw == null || raw === '') return {};

  const tryParse = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'object' && !Array.isArray(value)) return value;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const candidates = [
      trimmed,
      decodeHtmlEntitiesInUrl(trimmed),
      trimmed
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>'),
    ];

    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate);
        if (typeof parsed === 'string') {
          try {
            const nested = JSON.parse(parsed);
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested;
          } catch {
            /* continue */
          }
        }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
      } catch {
        /* try next */
      }
    }
    return null;
  };

  return tryParse(raw) || {};
};

/** Normalize configuration labels (fix &amp;amp; from older saves). */
export const normalizeConfigurationLabels = (configurations) => {
  if (!Array.isArray(configurations)) return [];
  return configurations
    .map((item) => decodeHtmlEntities(typeof item === 'string' ? item : String(item ?? '')))
    .map((item) => item.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
};

/**
 * Map saved configuration labels onto the canonical options for a project type.
 * Drops unknown/orphan values and dedupes — fixes edit showing 4 while details shows 5
 * when an old "&amp;amp; Play" entry was still in the database.
 */
export const sanitizeConfigurationsForProjectType = (
  configurations,
  projectType,
  optionsByType = {}
) => {
  const cleaned = normalizeConfigurationLabels(configurations);
  const options = Array.isArray(optionsByType?.[projectType])
    ? optionsByType[projectType]
    : [];

  if (!options.length) {
    return [...new Set(cleaned)];
  }

  const compact = (value) =>
    decodeHtmlEntities(String(value || ''))
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const matched = [];
  cleaned.forEach((label) => {
    const hit =
      options.find((option) => option === label) ||
      options.find((option) => compact(option) === compact(label));
    if (hit && !matched.includes(hit)) {
      matched.push(hit);
    }
  });
  return matched;
};

/**
 * Collect brochure PDFs from upcoming_project_data.brochures and/or legacy brochure_url.
 * @param {{ upcomingData?: any, brochureUrl?: string|string[]|null, forEdit?: boolean }} opts
 */
export const collectProjectBrochures = ({
  upcomingData = {},
  brochureUrl = null,
  forEdit = false,
} = {}) => {
  const data = parseUpcomingProjectData(upcomingData);
  const resolveUrl = (url) => {
    const cleaned = decodeHtmlEntitiesInUrl(String(url || '').trim());
    if (!cleaned) return '';
    return resolvePropertyMediaUrl(cleaned) || cleaned;
  };

  const fromEntry = (item, index) => {
    if (typeof item === 'string' && item.trim()) {
      const url = resolveUrl(item);
      if (!url) return null;
      const base = {
        name: `Brochure ${index + 1}.pdf`,
        url,
      };
      return forEdit
        ? { ...base, isExisting: true, id: `existing-${index}` }
        : base;
    }
    if (item && typeof item === 'object') {
      const rawUrl = item.url || item.file || item.href || '';
      const url = resolveUrl(rawUrl);
      if (!url) return null;
      const base = {
        name: item.name || `Brochure ${index + 1}.pdf`,
        url,
      };
      return forEdit
        ? { ...base, isExisting: true, id: `existing-${index}` }
        : base;
    }
    return null;
  };

  let entries = [];
  const fromJson = data.brochures;
  if (Array.isArray(fromJson) && fromJson.length > 0) {
    entries = fromJson.map(fromEntry).filter(Boolean);
  } else if (typeof fromJson === 'string' && fromJson.trim()) {
    try {
      const parsed = JSON.parse(fromJson);
      if (Array.isArray(parsed)) {
        entries = parsed.map(fromEntry).filter(Boolean);
      }
    } catch {
      /* ignore */
    }
  }

  if (entries.length === 0) {
    const fallback = brochureUrl || data.brochure || null;
    let urls = [];
    if (typeof fallback === 'string' && fallback.trim()) {
      try {
        const parsed = JSON.parse(fallback);
        urls = Array.isArray(parsed) ? parsed : [fallback];
      } catch {
        urls = [fallback];
      }
    } else if (Array.isArray(fallback)) {
      urls = fallback;
    }
    entries = urls.map(fromEntry).filter(Boolean);
  }

  return entries;
};

export const createExistingImageValidationStatus = (images = []) =>
  resolvePropertyImageUrls(images).map((imageUrl) => ({
    file: null,
    preview: imageUrl,
    status: 'approved',
    errorMessage: '',
    imageId: null,
    imageUrl,
  }));

/** Total images shown in add/edit forms (existing + newly selected). */
export const getPropertyFormImageCount = ({
  imageValidationStatus = [],
  formDataImages = [],
  imageFiles = [],
} = {}) => {
  if (imageValidationStatus.length > 0) return imageValidationStatus.length;
  if (Array.isArray(formDataImages) && formDataImages.length > 0) return formDataImages.length;
  return imageFiles.length;
};

/** Map API/listing fields into form-friendly media for edit mode. */
export const hydratePropertyFormMedia = (initialData) => {
  if (!initialData) {
    return {
      images: [],
      video: null,
      brochure: null,
      videoUrl: '',
      brochureUrl: '',
    };
  }

  const images = collectPropertyImageUrls(initialData);
  const orderedImages = orderImagesWithCoverFirst(images, initialData.cover_image);
  const videoUrlRaw = initialData.videoUrl || initialData.video_url || '';
  const brochureUrlRaw = initialData.brochureUrl || initialData.brochure_url || '';
  const videoUrl = resolvePropertyMediaUrl(videoUrlRaw) || videoUrlRaw;
  const brochureUrl = resolvePropertyMediaUrl(brochureUrlRaw) || brochureUrlRaw;

  return {
    images: orderedImages,
    video: buildVideoFromUrl(videoUrlRaw),
    brochure: buildBrochureFromUrl(brochureUrlRaw),
    videoUrl,
    brochureUrl,
  };
};

export const isBlobUrl = (url) => typeof url === 'string' && url.startsWith('blob:');
